import React from 'react';
import { Card } from '@mantine/core';
import { motion } from 'framer-motion';
import ErrorState from '@/components/ui/Error.State';
import ListFind from '@/components/ui/list/components/List.Find';
import ListFindEmpty from '@/components/ui/list/components/List.Find.Empty';
import ListBody from '@/components/ui/list/components/List.Body';
import ListHead from '@/components/ui/list/components/List.Head';
import ListLoading from '@/components/ui/list/components/List.Loading';
import ListStatus from '@/components/ui/list/components/List.Status';
import { listColumns } from '@/function/list/list.columns';
import { listEntries } from '@/function/list/list.entries';
import { useDelayedFlag } from '@/hooks/use.delayed.flag';
import { useElementWidth } from '@/hooks/use.element.width';
import { useListFind } from '@/hooks/use.list.find';
import { useListKeyboard } from '@/hooks/use.list.keyboard';
import { useVirtualWindow } from '@/hooks/use.virtual.window';
import { FADE } from '@/theme/motion';
import { formatNumber } from '@/utils/format.number';
import type {
    TypeListError,
    TypeListFrame,
    TypeListGroups,
    TypeListItemState,
    TypeListItemsEntry,
    TypeListLayout,
    TypeListMore,
    TypeListSkeleton,
} from '@/types/list/types.list';
import type { TypeListFindBound, TypeListFindConfig } from '@/types/list/types.list.find';
import type { TypeSelectionSide, TypeTableSelection } from '@/types/list/types.list.selection';
import './item.list.css';

type TypeListDensity = 'comfortable' | 'compact';

type Props<T> = {
    items: readonly T[];
    itemKey: (item: T) => string;
    renderItem: (item: T, state: TypeListItemState) => React.ReactNode;
    label: string;
    empty: React.ReactNode;
    itemLabel?: (item: T) => string;
    onItemClick?: (item: T) => void;
    currentKey?: string | null;
    selection?: TypeTableSelection<T>;
    selectionAt?: TypeSelectionSide;
    layout?: TypeListLayout;
    frame?: TypeListFrame;
    density?: TypeListDensity;
    groups?: TypeListGroups<T>;
    loading?: boolean;
    refreshing?: boolean;
    error?: TypeListError | null;
    skeleton?: TypeListSkeleton;
    header?: React.ReactNode;
    footer?: React.ReactNode;
    summary?: React.ReactNode;
    more?: TypeListMore;
    total?: number | null;
    virtualize?: false | { threshold?: number; estimate?: number };
    fill?: boolean;
    find?: TypeListFindConfig<T> | TypeListFindBound;
};

const ROWS: TypeListLayout = { type: 'rows' };
const SKELETON_ROW_REM = 3.5;

const isBound = <T,>(find: TypeListFindConfig<T> | TypeListFindBound | undefined): find is TypeListFindBound =>
    Boolean(find && 'kind' in find && find.kind === 'bound');

function ItemList<T>({
    items: source, itemKey, renderItem, label, empty, itemLabel, onItemClick, currentKey, selection, selectionAt = 'end',
    layout = ROWS, frame, density = 'comfortable', groups, loading = false, refreshing = false, error, skeleton,
    header, footer, summary, more, total, virtualize, fill = false, find,
}: Props<T>) {
    const bound = isBound(find) ? find : null;
    const own = useListFind(source, bound ? null : (find as TypeListFindConfig<T> | undefined) ?? null);
    const items = bound ? source : own.shown;
    const bar = bound?.bar ?? own.find?.bar ?? null;
    const resolvedFrame: TypeListFrame = frame ?? (layout.type === 'grid' ? 'plain' : 'card');
    const [widthRef, width] = useElementWidth<HTMLDivElement>();
    const bodyRef = React.useRef<HTMLDivElement | null>(null);
    const domId = React.useId();

    const columns = layout.type === 'grid' ? listColumns(width, layout.min, layout.gap) : 1;

    const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(() => new Set(groups?.initiallyCollapsed ?? []));
    const toggleGroup = React.useCallback((group: string) => setCollapsed((previous) => {
        const next = new Set(previous);
        if (next.has(group)) next.delete(group);
        else next.add(group);
        return next;
    }), []);

    const model = React.useMemo(() => listEntries({
        items,
        itemKey,
        columns,
        groupBy: groups?.by,
        groupOrder: groups?.order,
        collapsed,
    }), [items, itemKey, columns, groups?.by, groups?.order, collapsed]);

    const virtual = useVirtualWindow(bodyRef, model.entries.length, {
        estimate: virtualize ? virtualize.estimate : undefined,
        threshold: virtualize === false ? Number.POSITIVE_INFINITY : virtualize?.threshold,
    });

    const keys = React.useMemo(() => model.ordered.map(itemKey), [model.ordered, itemKey]);

    const mounted = React.useMemo(() => {
        const shown = virtual.active ? model.entries.slice(virtual.start, virtual.end) : model.entries;
        const cells = shown.filter((entry): entry is TypeListItemsEntry<T> => entry.kind === 'items');
        if (!cells.length) return { from: 0, to: 0 };
        const last = cells[cells.length - 1];
        return { from: cells[0].start, to: last.start + last.items.length };
    }, [model.entries, virtual.active, virtual.start, virtual.end]);

    const ordered = model.ordered;
    const onActivate = React.useMemo(
        () => (onItemClick ? (index: number) => { const item = ordered[index]; if (item !== undefined) onItemClick(item); } : undefined),
        [onItemClick, ordered],
    );
    const onToggle = React.useMemo(
        () => (selection ? (index: number, extend: boolean) => { const item = ordered[index]; if (item !== undefined) selection.toggle(item, extend); } : undefined),
        [selection, ordered],
    );

    const keyboard = useListKeyboard({
        containerRef: bodyRef,
        keys,
        columns,
        onActivate,
        onToggle,
        fallbackIndex: mounted.from,
        isMounted: React.useCallback((index: number) => index >= mounted.from && index < mounted.to, [mounted]),
    });

    const groupSizes = React.useMemo(() => {
        const sizes = new Map<string, number>();
        for (const entry of model.entries) if (entry.kind === 'group') sizes.set(entry.group, entry.count);
        return sizes;
    }, [model.entries]);

    const listSize = total ?? (more?.hasMore ? -1 : items.length);
    const setsizeOf = React.useCallback(
        (entry: TypeListItemsEntry<T>) => (entry.group === null ? listSize : groupSizes.get(entry.group) ?? entry.size),
        [listSize, groupSizes],
    );
    const groupDomId = React.useCallback((group: string) => `${domId}-g-${encodeURIComponent(group)}`, [domId]);

    const waiting = loading && items.length === 0;
    const showSkeleton = useDelayedFlag(waiting);

    const toolbar = bar ? <ListFind bar={bar} /> : null;

    if (!loading && items.length === 0 && error) {
        return <>{toolbar}<ErrorState title={error.title} body={error.body} onRetry={error.onRetry} retrying={error.retrying} /></>;
    }

    const autoSummary = summary ?? (total != null && total > items.length
        ? `${formatNumber(items.length)} su ${formatNumber(total)}`
        : undefined);

    const content = waiting ? (
        showSkeleton
            ? <ListLoading layout={layout} columns={columns} skeleton={skeleton} label={`Carico: ${label}`} />
            : <div aria-busy aria-label={`Carico: ${label}`} style={{ minHeight: `${(skeleton?.rows ?? 6) * SKELETON_ROW_REM}rem` }} />
    ) : items.length === 0 ? (
        <motion.div variants={FADE} initial="hidden" animate="visible">{bar?.filtered ? <ListFindEmpty bar={bar} /> : empty}</motion.div>
    ) : (
        <>
            <ListHead selection={selection} selectionAt={selectionAt}>{header}</ListHead>
            <div ref={bodyRef} data-bd-list="items" style={{ minWidth: 0 }}>
                <ListBody
                    label={label}
                    entries={model.entries}
                    virtual={virtual}
                    layout={layout}
                    columns={columns}
                    itemKey={itemKey}
                    itemLabel={itemLabel}
                    renderItem={renderItem}
                    groups={groups}
                    onToggleGroup={toggleGroup}
                    keyboard={keyboard}
                    currentKey={currentKey}
                    selection={selection}
                    selectionAt={selectionAt}
                    setsizeOf={setsizeOf}
                    onActivate={onActivate}
                    onToggle={onToggle}
                    groupDomId={groupDomId}
                />
            </div>
            <ListStatus summary={autoSummary} more={more} refreshing={refreshing} error={error ?? undefined} />
            {footer}
        </>
    );

    const shared = {
        ref: widthRef,
        className: 'bd-list',
        'data-frame': resolvedFrame,
        'data-layout': layout.type,
        'data-density': density,
        style: { minWidth: 0, ...(fill ? { flex: '1 0 auto' } : {}) } as React.CSSProperties,
    };

    if (resolvedFrame === 'card') {
        return (
            <>
                {toolbar}
                <Card {...shared} padding={0} radius="lg" withBorder style={{ ...shared.style, overflow: 'clip' }}>
                    {content}
                </Card>
            </>
        );
    }

    return <>{toolbar}<div {...shared}>{content}</div></>;
}

export default ItemList;
